/**
 * LocationTree Component
 *
 * Hierarchical tree view for managing location taxonomy.
 */

import { useState } from 'react';
import type { Location } from '../hooks';

interface LocationTreeProps {
  locations: Location[];
  onEdit?: (location: Location) => void;
  onAdd?: (parentId?: string) => void;
}

export const LocationTree = ({ locations, onEdit, onAdd }: LocationTreeProps) => {
  const [expanded, setExpanded] = useState<string[]>(['loc-1', 'loc-3']); // Lebanon and Mount Lebanon expanded by default

  const toggleExpand = (id: string) => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getTypeColor = (type: Location['type']) => {
    switch (type) {
      case 'country':
        return 'bg-blue-100 text-blue-700';
      case 'governorate':
        return 'bg-green-100 text-green-700';
      case 'district':
        return 'bg-amber-100 text-amber-700';
      case 'city':
        return 'bg-purple-100 text-purple-700';
    }
  };

  const getTypeIcon = (type: Location['type']) => {
    switch (type) {
      case 'country':
        return '🌍';
      case 'governorate':
        return '🏛️';
      case 'district':
        return '📍';
      case 'city':
        return '🏘️';
    }
  };

  const renderNode = (location: Location, level: number = 0) => {
    const children = locations.filter((l) => l.parentId === location.id);
    const isExpanded = expanded.includes(location.id);
    const hasChildren = children.length > 0;

    return (
      <div key={location.id}>
        <div
          className={`flex items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors ${
            level > 0 ? 'ml-' + level * 6 : ''
          }`}
          style={{ marginLeft: level * 24 }}
        >
          {/* Expand/Collapse button */}
          <button
            onClick={() => hasChildren && toggleExpand(location.id)}
            className={`w-6 h-6 flex items-center justify-center rounded ${
              hasChildren ? 'hover:bg-gray-200 cursor-pointer' : 'cursor-default'
            }`}
            disabled={!hasChildren}
          >
            {hasChildren ? (
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${
                  isExpanded ? 'rotate-90' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            ) : (
              <span className="w-4" />
            )}
          </button>

          {/* Type icon */}
          <span className="text-lg">{getTypeIcon(location.type)}</span>

          {/* Name and type badge */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{location.name}</span>
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${getTypeColor(
                  location.type
                )}`}
              >
                {location.type}
              </span>
            </div>
            {location.coordinates && (
              <p className="text-xs text-gray-500 mt-0.5">
                {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
              </p>
            )}
          </div>

          {/* Listing count */}
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              {location.listingCount}
            </p>
            <p className="text-xs text-gray-500">listings</p>
          </div>

          {/* Actions */}
          <div className="flex gap-1 ml-2">
            {onEdit && (
              <button
                onClick={() => onEdit(location)}
                className="p-2 text-gray-400 hover:text-primary-500 hover:bg-gray-100 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
            {onAdd && location.type !== 'city' && (
              <button
                onClick={() => onAdd(location.id)}
                className="p-2 text-gray-400 hover:text-green-500 hover:bg-gray-100 rounded"
                title={`Add ${
                  location.type === 'country'
                    ? 'governorate'
                    : location.type === 'governorate'
                    ? 'district'
                    : 'city'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Render children */}
        {isExpanded && children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  const rootNodes = locations.filter((l) => !l.parentId);

  if (locations.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg
          className="w-12 h-12 mx-auto mb-3 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <p>No locations configured</p>
        {onAdd && (
          <button
            onClick={() => onAdd()}
            className="mt-2 text-primary-500 hover:text-primary-600"
          >
            Add your first location
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Legend */}
      <div className="flex gap-4 mb-4 text-xs">
        <span className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded-full ${getTypeColor('country')}`}>
            Country
          </span>
        </span>
        <span className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded-full ${getTypeColor('governorate')}`}>
            Governorate
          </span>
        </span>
        <span className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded-full ${getTypeColor('district')}`}>
            District
          </span>
        </span>
        <span className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded-full ${getTypeColor('city')}`}>
            City
          </span>
        </span>
      </div>

      {/* Tree */}
      {rootNodes.map((node) => renderNode(node))}

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200 flex gap-6 text-sm text-gray-500">
        <span>
          <strong className="text-gray-900">
            {locations.filter((l) => l.type === 'governorate').length}
          </strong>{' '}
          governorates
        </span>
        <span>
          <strong className="text-gray-900">
            {locations.filter((l) => l.type === 'district').length}
          </strong>{' '}
          districts
        </span>
        <span>
          <strong className="text-gray-900">
            {locations.filter((l) => l.type === 'city').length}
          </strong>{' '}
          cities
        </span>
      </div>
    </div>
  );
};

export default LocationTree;
